import * as React from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTheme, alpha } from "@mui/material/styles";

import { default as DataGrid } from "../../../components/LocalizedDataGrid";

const getFieldIssueFromSafeParseResult = (fieldName, safeParseResult) => {
    const issues = safeParseResult?.error?.issues;
    const fieldIssue = issues?.find(issue => issue.path[0] === fieldName);
    return fieldIssue ? fieldIssue.message.split(":")[0] : null;
}

const renderCellFromCsvAndDtoApiStatus = (csvFieldName, dtoFieldName, theme) => {
    return (params) => {
        const csvResult = params.row.csvResult;
        const dtoResult = params.row.dtoResult;
        const apiStatusResult = params.row.apiStatus;

        const csvValue = csvResult?.success ? csvResult.data?.[csvFieldName] : null;
        const csvIssue = csvResult && !csvResult.success
            ? getFieldIssueFromSafeParseResult(csvFieldName, csvResult)
            : null;
        const dtoIssue = dtoResult && !dtoResult.success
            ? getFieldIssueFromSafeParseResult(dtoFieldName, dtoResult)
            : null;
        const apiStatus = apiStatusResult?.status;
        const fieldSpecificApiMessage = apiStatusResult?.response?.[dtoFieldName];

        const errorBg = alpha(theme.palette.error.main, 0.12);
        const errorFg = theme.palette.error.main;

        if (csvIssue) {
            return (
                <Box sx={{ backgroundColor: errorBg }}>
                    <Box>{csvValue}</Box>
                    <Box sx={{ fontSize: "0.75rem", color: errorFg }}>
                        {csvIssue}
                    </Box>
                </Box>
            );
        }

        if (dtoIssue) {
            return (
                <Box sx={{ backgroundColor: errorBg }}>
                    <Box>{csvValue}</Box>
                    <Box sx={{ fontSize: "0.75rem", color: errorFg }}>
                        {dtoIssue}
                    </Box>
                </Box>
            );
        }

        if (apiStatus === "Error" && fieldSpecificApiMessage) {
            return (
                <Box sx={{ backgroundColor: errorBg }}>
                    <Box>{csvValue}</Box>
                    <Box sx={{ fontSize: "0.75rem", color: errorFg }}>
                        {apiStatus + ": "}{fieldSpecificApiMessage}
                    </Box>
                </Box>
            );
        }

        return <Box>{csvValue}</Box>;
    };
}

export default function CsvDataGrid(props) {
    const {
        rowsData = [],
        apiStatusesByRowId = {},
        dataColumns = [],
        components,
        componentsProps,
        containerSx = {
            height: "calc(100vh - 300px)",
            width: "100%",
        },
        pendingColor,
    } = props;

    const { t } = useTranslation();
    const theme = useTheme();

    const rows = React.useMemo(() => {
        return rowsData.map(row => ({
            ...row,
            apiStatus: apiStatusesByRowId[row.rowId]
        }));
    }, [rowsData, apiStatusesByRowId]);

    const columns = React.useMemo(() => {
        const statusColumn = {
            field: "statusText",
            headerName: t("Import Status"),
            flex: 1,
            minWidth: 140,
            valueGetter: (p) => p.row.apiStatus ?? "",
            renderCell: (params) => {
                const status = params.row.apiStatus?.status;
                const message = params.row.apiStatus?.message;

                if (status === "Success") {
                    return <Box sx={{ color: theme.palette.success.main }}>{t("Success")}</Box>;
                }

                if (status === "Error") {
                    return (
                        <Box sx={{ color: theme.palette.error.main }}>
                            {t("Error")} {message ? ": " + message : t("in specific field")}
                        </Box>
                    );
                }

                if (status === "Invalid") {
                    return (
                        <Box sx={{ color: theme.palette.error.main }}>
                            {t("Invalid")}: {message}
                        </Box>
                    );
                }

                return <Box sx={{ color: pendingColor ?? theme.palette.info.main }}>{t("Pending")}</Box>;
            },
        };

        const mappedDataColumns = dataColumns.map((column) => {
            const {
                csvFieldName,
                dtoFieldName,
                renderCell,
                ...restColumn
            } = column;

            return {
                ...restColumn,
                renderCell: renderCell ?? renderCellFromCsvAndDtoApiStatus(csvFieldName, dtoFieldName ?? csvFieldName, theme),
            };
        });

        return [statusColumn, ...mappedDataColumns];
    }, [t, theme, dataColumns, pendingColor]);

    return (
        <Box
            sx={{
                ...containerSx,
                "& .actions": { color: "text.secondary" },
                "& .textPrimary": { color: "text.primary" },
            }}
        >
            <DataGrid
                rows={rows}
                columns={columns}
                experimentalFeatures={{ newEditingApi: true }}
                getRowId={(row) => row.rowId}
                components={components}
                componentsProps={componentsProps}
            />
        </Box>
    );
}
